import React from 'react';

const Y_AXIS_SEPARATOR_DROPDOWN_STYLES = `
    .ant-select-dropdown .ant-select-item-divider {
        height: 1px !important;
        margin: 4px 0 !important;
        padding: 0 !important;
        min-height: 1px !important;
        background-color: #D9D9D9 !important;
        border: none !important;
        cursor: default !important;
        pointer-events: none !important;
    }
    .ant-select-dropdown .ant-select-item-divider:hover {
        background-color: #D9D9D9 !important;
    }
    .ant-select-dropdown .ant-select-item-divider .ant-select-item-option-content {
        display: none !important;
    }
`;

/** Matches Ask Asper selection-strip action buttons (AgenticRightPanel). */
const CHART_FILTER_DROPDOWN_ACTION_BUTTON_CLASS =
    'px-2 h-6 flex items-center justify-center bg-transparent border-none cursor-pointer rounded hover:bg-[rgba(95,73,188,0.1)] transition-colors';

const CHART_FILTER_DROPDOWN_ACTION_LABEL_CLASS =
    'font-sans font-medium text-sm leading-[20px] text-[#5f49bc] underline';

export type ChartFilterOption = {
    value: string | number;
    disabled?: boolean;
};

export type GroupedSelectOption = {
    label: string;
    value: string;
    disabled?: boolean;
    className?: string;
};

export type YAxisMetricOption = {
    label: string;
    value: string;
    familyIndex: number;
};

function groupYAxisMetricsByFamily(
    yOptions: YAxisMetricOption[],
): Map<number, YAxisMetricOption[]> {
    const familyGroups = new Map<number, YAxisMetricOption[]>();
    yOptions.forEach((option) => {
        if (!familyGroups.has(option.familyIndex)) {
            familyGroups.set(option.familyIndex, []);
        }
        familyGroups.get(option.familyIndex)!.push(option);
    });
    return familyGroups;
}

export function buildY1GroupedOptions(
    yOptions: YAxisMetricOption[],
    y1Selection: string[],
    y2Selection: string[],
): GroupedSelectOption[] {
    const groupedOptions: GroupedSelectOption[] = [];
    const y1FamilyIndices = new Set(y1Selection.map((m) => Number(m.split('_')[0])));
    const y2FamilyIndices = new Set(y2Selection.map((m) => Number(m.split('_')[0])));
    const hasY1Selection = y1Selection.length > 0;

    let isFirstFamily = true;
    groupYAxisMetricsByFamily(yOptions).forEach((metrics, familyIndex) => {
        if (!isFirstFamily) {
            groupedOptions.push({
                label: '',
                value: `__separator_${familyIndex}__`,
                disabled: true,
                className: 'ant-select-item-divider',
            });
        }
        isFirstFamily = false;

        metrics.forEach((metric) => {
            groupedOptions.push({
                label: metric.label,
                value: metric.value,
                disabled:
                    y2FamilyIndices.has(metric.familyIndex) ||
                    (hasY1Selection && !y1FamilyIndices.has(metric.familyIndex)),
            });
        });
    });

    return groupedOptions;
}

export function buildY2GroupedOptions(
    yOptions: YAxisMetricOption[],
    y1Selection: string[],
    y2Selection: string[],
): GroupedSelectOption[] {
    const groupedOptions: GroupedSelectOption[] = [];
    const y1FamilyIndices = new Set(y1Selection.map((m) => Number(m.split('_')[0])));
    const y2FamilyIndices = new Set(y2Selection.map((m) => Number(m.split('_')[0])));
    const hasY2Selection = y2Selection.length > 0;

    let isFirstFamily = true;
    groupYAxisMetricsByFamily(yOptions).forEach((metrics, familyIndex) => {
        if (!isFirstFamily) {
            groupedOptions.push({
                label: '',
                value: `__separator_${familyIndex}__`,
                disabled: true,
                className: 'ant-select-item-divider',
            });
        }
        isFirstFamily = false;

        metrics.forEach((metric) => {
            groupedOptions.push({
                label: metric.label,
                value: metric.value,
                disabled:
                    y1FamilyIndices.has(metric.familyIndex) ||
                    (hasY2Selection && !y2FamilyIndices.has(metric.familyIndex)),
            });
        });
    });

    return groupedOptions;
}

export type ChartFilterDropdownActionsConfig = {
    onReset: () => void;
    onSelectAll: () => void;
    resetLabel?: string;
    selectAllLabel?: string;
    dataTestIdPrefix?: string;
};

function getMetricFamilyIndex(metricId: string): number {
    return Number(metricId.split('_')[0]);
}

/** Non-disabled option values, excluding Y-axis family separators. */
export function getSelectableOptionValues(options: ChartFilterOption[]): string[] {
    return options
        .filter((option) => !option.disabled && !String(option.value).startsWith('__separator_'))
        .map((option) => String(option.value));
}

function getFirstMetricFamilyIndex(options: ChartFilterOption[]): number | undefined {
    const enabledFamilies = getSelectableOptionValues(options).map(getMetricFamilyIndex);
    if (enabledFamilies.length === 0) return undefined;
    return Math.min(...enabledFamilies);
}

/**
 * Y-axis Select All: all enabled metrics in the active family when something is selected;
 * otherwise all enabled metrics from the first metric family.
 */
export function getYAxisSelectAllValues(
    options: ChartFilterOption[],
    currentSelection: string[],
): string[] {
    const enabled = options.filter(
        (option) => !option.disabled && !String(option.value).startsWith('__separator_'),
    );
    if (enabled.length === 0) return [];

    const familyIndex =
        currentSelection.length > 0
            ? getMetricFamilyIndex(currentSelection[0])
            : getFirstMetricFamilyIndex(options);

    if (familyIndex === undefined) return [];

    return enabled
        .filter((option) => getMetricFamilyIndex(String(option.value)) === familyIndex)
        .map((option) => String(option.value));
}

export function chartFilterDropdownRender(
    menu: React.ReactElement,
    actions: ChartFilterDropdownActionsConfig,
    options?: { includeYAxisSeparatorStyles?: boolean },
): React.ReactElement {
    const {
        onReset,
        onSelectAll,
        resetLabel = 'Reset',
        selectAllLabel = 'Select All',
        dataTestIdPrefix,
    } = actions;

    return (
        <>
            <div
                className="flex items-center justify-between px-2 py-2 border-b border-[rgba(0,0,0,0.06)]"
                onMouseDown={(event) => event.preventDefault()}
            >
                <button
                    type="button"
                    className={CHART_FILTER_DROPDOWN_ACTION_BUTTON_CLASS}
                    onClick={(event) => {
                        event.stopPropagation();
                        onReset();
                    }}
                    data-test-id={dataTestIdPrefix ? `${dataTestIdPrefix}-reset` : undefined}
                >
                    <span className={CHART_FILTER_DROPDOWN_ACTION_LABEL_CLASS}>{resetLabel}</span>
                </button>
                <button
                    type="button"
                    className={CHART_FILTER_DROPDOWN_ACTION_BUTTON_CLASS}
                    onClick={(event) => {
                        event.stopPropagation();
                        onSelectAll();
                    }}
                    data-test-id={dataTestIdPrefix ? `${dataTestIdPrefix}-select-all` : undefined}
                >
                    <span className={CHART_FILTER_DROPDOWN_ACTION_LABEL_CLASS}>{selectAllLabel}</span>
                </button>
            </div>
            {menu}
            {options?.includeYAxisSeparatorStyles ? (
                <style>{Y_AXIS_SEPARATOR_DROPDOWN_STYLES}</style>
            ) : null}
        </>
    );
}
